import { EventSource } from "eventsource";
import { useEffect, useMemo, useState } from "react";
import { client } from "./generated/index.js";
const isParameterizedSseEndpoint = (endpoint) => endpoint.includes("{") && endpoint.includes("}");
const fetchWithClientHeaders = (abortSignal) => (input) => fetch(input, {
    headers: client.getConfig().headers,
    signal: abortSignal,
});
export const useSSE = (endpoint, pathParametersOrOptions, options) => {
    let path;
    let actualOptions;
    if (isParameterizedSseEndpoint(endpoint)) {
        path = buildEndpointUrl(endpoint, pathParametersOrOptions);
        actualOptions = options ?? {};
    }
    else {
        path = endpoint;
        actualOptions =
            pathParametersOrOptions ?? {};
    }
    if (actualOptions.query) {
        path = `${path}?${new URLSearchParams(actualOptions.query).toString()}`;
    }
    const [data, setData] = useState(undefined);
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [graceElapsed, setGraceElapsed] = useState(false);
    const url = useMemo(() => new URL(path, actualOptions.baseUrl ?? client.getConfig().baseUrl), [path, actualOptions.baseUrl]);
    // Memoizar el token de autenticación
    // Si se proporciona authToken en las opciones, usarlo (reactivo a cambios del AuthProvider)
    // Si no, usar el token del cliente (solo se lee una vez al montar)
    const authToken = useMemo(() => {
        if (actualOptions.authToken !== undefined) {
            return actualOptions.authToken;
        }
        const config = client.getConfig();
        const headers = config.headers;
        return headers?.Authorization ?? "";
    }, [actualOptions.authToken]);
    useEffect(() => {
        if (actualOptions.disabled) {
            setIsLoading(false);
            setGraceElapsed(false);
            return;
        }
        setError(false);
        setIsLoading(true);
        setGraceElapsed(false);
        const abortController = new AbortController();
        const eventSource = new EventSource(url, {
            fetch: fetchWithClientHeaders(abortController.signal),
        });
        // Contador de errores consecutivos para detectar problemas de autenticación
        // EventSource no expone códigos HTTP, así que detectamos 401 por errores repetidos
        let consecutiveErrorCount = 0;
        const MAX_CONSECUTIVE_ERRORS = 3;
        let graceTimer;
        // El presupuesto de gracia es único por conexión lógica (todo el ciclo de
        // vida de este efecto, reconexiones automáticas de EventSource incluidas).
        // Solo se arranca una vez, en la primera apertura; una reconexión interna
        // no debe alargarlo ni reiniciarlo.
        let graceStarted = false;
        eventSource.onopen = () => {
            consecutiveErrorCount = 0; // Resetear contador en conexión exitosa
            setError(false);
            setIsLoading(false);
            if (actualOptions.graceMs !== undefined && !graceStarted) {
                graceStarted = true;
                graceTimer = setTimeout(() => setGraceElapsed(true), actualOptions.graceMs);
            }
        };
        eventSource.onerror = () => {
            consecutiveErrorCount++;
            // Si hay muchos errores consecutivos, probablemente es un error de autenticación
            if (consecutiveErrorCount >= MAX_CONSECUTIVE_ERRORS &&
                actualOptions.onAuthError) {
                eventSource.close();
                abortController.abort();
                actualOptions.onAuthError();
                return;
            }
            setError(true);
            setIsLoading(false);
        };
        eventSource.onmessage = (messageEvent) => {
            consecutiveErrorCount = 0; // Resetear contador en mensaje exitoso
            clearTimeout(graceTimer);
            setGraceElapsed(false);
            try {
                const raw = JSON.parse(messageEvent.data);
                const mapped = actualOptions.map ? actualOptions.map(raw) : raw;
                // High-frequency streams: Use onMessage callback (recommended)
                if (actualOptions.onMessage) {
                    actualOptions.onMessage(mapped);
                }
                // Low-frequency streams: Update React state (may lose events at high frequency)
                setData(mapped);
            }
            catch (e) {
                // eslint-disable-next-line no-console
                console.error("Error parsing/mapping SSE message:", e);
            }
            finally {
                setError(false);
                setIsLoading(false);
            }
        };
        return () => {
            clearTimeout(graceTimer);
            eventSource.close();
            abortController.abort();
        };
    }, [
        url,
        authToken,
        actualOptions.disabled,
        actualOptions.map,
        actualOptions.onMessage,
        actualOptions.onAuthError,
        actualOptions.graceMs,
    ]);
    return { data, error, isLoading, graceElapsed };
};
const buildEndpointUrl = (endpoint, pathParameters) => {
    let url = endpoint;
    for (const [key, value] of Object.entries(pathParameters ?? {})) {
        url = url.replaceAll(`{${key}}`, String(value));
    }
    return url;
};
//# sourceMappingURL=sse.js.map